// workshops.ts — per-line workshop (SST) resolution.
//
// Indicative only: the workshop choice is stored on the line (workshopResp) but never
// changes PV / total / target margin. Data comes from bindEngineData().
import {
  MAT_FORMATS,
  SERVICE_KEY,
  WORKSHOP_MATRIX,
  WORKSHOP_MAPPING,
  MATERIAL_CATEGORY_MAP,
  ALL_WORKSHOPS,
  UNFILTERED_FAMILIES,
  engineSettings,
} from "../../data";
import { paSupplierLookup, paMode } from "../pricing/pa-margins";
import type { QuoteLine } from "../core/core";
import { engineMessage } from "../core/messages";
import { isTierRegime, isTransferRegime, isJoineryFabRegime, isUnitRegime, isLargeFormatPrintRegime } from "../core/regimes";

export {
  WORKSHOP_MATRIX,
  WORKSHOP_MAPPING,
  MATERIAL_CATEGORY_MAP,
  ALL_WORKSHOPS,
} from "../../data";

// R1: contour cutting = cuts that require a traced path (shape, complex, letters).
const CONTOUR_CUT_REFS = new Set(["CUT-1503", "CUT-1504", "CUT-1505", "CUT-1506", "CUT-1508", "CUT-1509"]);


/**
 * Is the material available in a 5-metre width (R2/R3)? Only the workshop owning the machine can
 * produce a piece that wide.
 *
 * @param matRef The material ref.
 * @returns True when one of the material's formats is at least 5 m wide.
 */
function webWidth5m(matRef?: string): boolean {
  if (!matRef) return false;
  const formats = (MAT_FORMATS as Record<string, readonly string[]>)[matRef] ?? [];
  return formats.some((x) => (parseInt(String(x), 10) || 0) >= engineSettings().parameters.wideWebThresholdMm);
}

/**
 * SST family of a line (R0 plus R1 > R2/R3 exceptions; R4 short-circuits upstream). Transfer is
 * checked before stand because TRANSFER also belongs to the tier regime.
 *
 * @param l The quote line.
 * @returns The family, or null on fallback (unmapped category: full menu, empty cell).
 */
export function resolveSstFamily(l: QuoteLine): string | null {
  if (isTransferRegime(l)) return "Transfert mini";
  if (isTierRegime(l)) return "Stand";
  if (isJoineryFabRegime(l)) return "MENUIS & FAB";
  if (isUnitRegime(l)) return "SUR MESURE";
  const category = l.material ? MATERIAL_CATEGORY_MAP[l.material] : undefined;
  const mappedFamily = category ? WORKSHOP_MAPPING[category] ?? null : null;
  if (mappedFamily == null) return null;
  if (mappedFamily === "adhésif" && l.cut && CONTOUR_CUT_REFS.has(l.cut)) return "adhésifs + DAO";
  if (mappedFamily === "baches" && webWidth5m(l.material)) return "bache 5 M";
  if (mappedFamily === "toiles" && webWidth5m(l.material)) return "toiles UV 5M";
  return mappedFamily;
}

export interface WorkshopMenu {
  /** Rank-1 workshop (pre-filled, R8), null on fallback. */
  rank1: string | null;
  /** Menu options ordered rank 1, then rank 2, then allowed alphabetically. */
  options: { label: string; rank: number; viaPaGrid?: boolean }[];
}

/**
 * Workshop menu for a family (R6). Null or unfiltered family yields all workshops alphabetically, no default.
 *
 * @param family The SST family, or null on fallback.
 * @returns The menu with its rank-1 default.
 */
export function workshopMenu(family: string | null): WorkshopMenu {
  if (family == null || UNFILTERED_FAMILIES.has(family)) {
    return { rank1: null, options: ALL_WORKSHOPS.map((label) => ({ label, rank: 3 })) };
  }
  const options = [...(WORKSHOP_MATRIX[family] ?? [])];
  return { rank1: options.find((o) => o.rank === 1)?.label ?? null, options };
}

/**
 * Workshop menu for a line: the family matrix plus any workshop holding a PA grid for this
 * combination (a negotiated price proves capability). Grid-derived workshops enter as allowed
 * (rank 3), marked `viaPaGrid`; the rank-1 default is never changed.
 *
 * @param l The quote line.
 * @returns The menu for this line.
 */
export function lineWorkshopMenu(l: QuoteLine): WorkshopMenu {
  const base = workshopMenu(resolveSstFamily(l));
  if (!isLargeFormatPrintRegime(l) || !l.material) return base;
  const existingLabels = new Set(base.options.map((o) => o.label));
  const mode = paMode(l);
  const viaGrid: WorkshopMenu["options"] = [];
  for (const label of Object.keys(SERVICE_KEY)) {
    if (!label || existingLabels.has(label)) continue;
    if (paSupplierLookup({ ...l, workshopResp: label }, mode)) viaGrid.push({ label, rank: 3, viaPaGrid: true });
  }
  if (!viaGrid.length) return base;
  // Ranks 1 and 2 first (matrix order), then all allowed ones alphabetically (matrix + grids).
  const topRanked = base.options.filter((o) => o.rank <= 2);
  const allowed = [...base.options.filter((o) => o.rank > 2), ...viaGrid].sort((a, b) => a.label.localeCompare(b.label));
  return { rank1: base.rank1, options: [...topRanked, ...allowed] };
}

/**
 * Pre-filled workshop for a line (R8): rank 1 of the resolved family, or "" on fallback.
 *
 * @param l The quote line.
 * @returns The default workshop label.
 */
export function defaultWorkshop(l: QuoteLine): string {
  return workshopMenu(resolveSstFamily(l)).rank1 ?? "";
}

/** One editable row of the families × workshops matrix (admin screen shape). */
export interface MatrixRow { family: string; workshop: string; rank: number }

/**
 * Validates the matrix before saving; returns the list of violations (empty = OK).
 * Two invariants with distinct messages: structural (exactly one rank 1, at most one rank 2
 * per family) and coverage (every active item has a capable workshop, via family or PA grid).
 *
 * @param lines The matrix rows to validate.
 * @param activeFamilies Families carrying at least one active item (must be covered).
 * @param familiesWithGrid Families where a PA grid already opens a workshop (covered without a matrix row).
 * @returns The violation messages, empty when valid.
 */
export function validateMatrix(
  lines: readonly MatrixRow[],
  activeFamilies: readonly string[] = [],
  familiesWithGrid: readonly string[] = [],
): string[] {
  const errors: string[] = [];
  const byFamily = new Map<string, MatrixRow[]>();
  for (const l of lines) {
    if (!byFamily.has(l.family)) byFamily.set(l.family, []);
    byFamily.get(l.family)!.push(l);
  }
  // Structural (R11) — on every family present in the matrix.
  for (const [fam, familyRows] of byFamily) {
    const rank1Count = familyRows.filter((o) => o.rank === 1).length;
    const rank2Count = familyRows.filter((o) => o.rank === 2).length;
    if (rank1Count !== 1) {
      errors.push(engineMessage("engineWorkshopR11Rank1", { family: fam, count: rank1Count }));
    }
    if (rank2Count > 1) {
      errors.push(engineMessage("engineWorkshopR11Rank2", { family: fam, count: rank2Count }));
    }
  }
  // Coverage — every active family has at least one workshop (matrix) or a PA grid.
  const withGrid = new Set(familiesWithGrid);
  for (const fam of activeFamilies) {
    const count = byFamily.get(fam)?.length ?? 0;
    if (count === 0 && !withGrid.has(fam)) {
      errors.push(engineMessage("engineWorkshopCoverage", { family: fam }));
    }
  }
  return errors;
}
